/**
 * EB-1B Visa Criteria Definitions
 *
 * The EB-1B is an immigrant visa for outstanding professors or researchers
 * with international recognition and at least 3 years of experience.
 *
 * Legal Reference: 8 CFR 204.5(i), https://www.uscis.gov/policy-manual/volume-6-part-f-chapter-3
 */

import { VisaCriterion } from './types';

/**
 * EB-1B visa criteria
 *
 * The beneficiary must meet at least 2 out of 6 criteria AND have:
 * - At least 3 years of experience in teaching or research
 * - A job offer for a permanent research position or tenure-track teaching position
 * - International recognition as outstanding in a specific academic field
 */
export const EB1B_CRITERIA: VisaCriterion[] = [
  {
    id: 'eb1b_awards',
    name: 'Awards for Outstanding Achievement',
    description: 'Receipt of major prizes or awards for outstanding achievement in the academic field',
    weight: 9,
    requiredDocuments: [
      'AWARD_CERTIFICATE',
      'GRANT_APPROVAL',
      'COMPETITION_RESULTS'
    ],
    legalReference: '8 CFR 204.5(i)(3)(i)(A)',
    minimumEvidence: 2
  },
  {
    id: 'eb1b_membership',
    name: 'Professional Memberships',
    description: 'Membership in associations in the academic field that require outstanding achievements of their members',
    weight: 8,
    requiredDocuments: [
      'MEMBERSHIP_CERTIFICATE',
      'PROFESSIONAL_LICENSE'
    ],
    legalReference: '8 CFR 204.5(i)(3)(i)(B)',
    minimumEvidence: 1
  },
  {
    id: 'eb1b_published_material',
    name: 'Published Material About Work',
    description: 'Published material in professional publications written by others about the beneficiary\'s work in the academic field. Must include title, date, and author of material',
    weight: 9,
    requiredDocuments: [
      'MEDIA_ARTICLE',
      'PRESS_RELEASE',
      'ACADEMIC_REVIEW'
    ],
    legalReference: '8 CFR 204.5(i)(3)(i)(C)',
    minimumEvidence: 3
  },
  {
    id: 'eb1b_judging',
    name: 'Judging Others\' Work',
    description: 'Participation, either individually or on a panel, as the judge of the work of others in the same or an allied academic field',
    weight: 8,
    requiredDocuments: [
      'JUDGING_INVITATION',
      'PEER_REVIEW_CONFIRMATION'
    ],
    legalReference: '8 CFR 204.5(i)(3)(i)(D)',
    minimumEvidence: 2
  },
  {
    id: 'eb1b_research_contributions',
    name: 'Original Research Contributions',
    description: 'Original scientific or scholarly research contributions to the academic field',
    weight: 10,
    requiredDocuments: [
      'RESEARCH_PAPER',
      'PATENT_DOCUMENT',
      'INNOVATION_DESCRIPTION',
      'CITATION_REPORT'
    ],
    legalReference: '8 CFR 204.5(i)(3)(i)(E)',
    minimumEvidence: 2
  },
  {
    id: 'eb1b_scholarly_articles',
    name: 'Scholarly Articles',
    description: 'Authorship of scholarly books or articles (in scholarly journals with international circulation) in the academic field',
    weight: 9,
    requiredDocuments: [
      'PEER_REVIEWED_ARTICLE',
      'SCHOLARLY_BOOK',
      'CONFERENCE_PROCEEDINGS'
    ],
    legalReference: '8 CFR 204.5(i)(3)(i)(F)',
    minimumEvidence: 3
  }
];

/**
 * Get EB-1B criteria configuration
 */
export const EB1B_CONFIG = {
  visaType: 'EB-1B',
  criteria: EB1B_CRITERIA
};
